import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ThemeProvider, useTheme, StatCard, SectionCard,
  Topbar, LoadingScreen, EmptyState, StatusBadge, LiveDot, UserProfileCard
} from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

// Timer for pending complaints (verification deadline) and verified complaints (resolution deadline)
function ComplaintTimer({ deadline, isOverdue, label }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);

  if (!deadline) return null;
  const diff = new Date(deadline && !deadline.endsWith('Z') ? deadline + 'Z' : deadline).getTime() - now;
  const over = isOverdue || diff < 0;
  const abs = Math.abs(diff);
  const h = Math.floor(abs / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  const s = Math.floor((abs % 60000) / 1000);
  const timeStr = h > 0 ? h + 'h ' + m + 'm ' + s + 's' : m + 'm ' + s + 's';
  const color = over ? '#ef4444' : diff < 3600000 ? '#f59e0b' : '#10b981';
  const icon = over ? '\u26A0\uFE0F' : '\u23F1';

  return (
    <span style={{ fontSize: 10, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums',
      display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 3 }}>
      {icon} {over ? '+' + timeStr : timeStr}
      {over && label && <span style={{ marginLeft: 4, fontSize: 9, opacity: 0.8 }}>{label}</span>}
    </span>
  );
}

function DashboardInner() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [myTab, setMyTab] = useState('dept');
  const [community, setCommunity] = useState(null);
  const [communityLoading, setCommunityLoading] = useState(true);
  const [communityTab, setCommunityTab] = useState('dept');
  const [voteAnim, setVoteAnim] = useState({});
  const [votePlus, setVotePlus] = useState({});

  const fetchData = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await api.get('/dashboard/user/' + user.id + '/complaints');
      setComplaints(Array.isArray(res.data) ? res.data : []);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load complaints');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const fetchCommunity = useCallback(async () => {
    try {
      const res = await api.get('/community/');
      setCommunity(res.data);
    } catch (e) {
      console.error('Community load failed', e);
    } finally {
      setCommunityLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchCommunity();
  }, [fetchData, fetchCommunity]);

  const handleUpvote = async (complaintId) => {
    const all = (community?.dept_complaints || []).concat(community?.uni_complaints || []);
    const wasVoted = all.find(c => c.id === complaintId)?.user_has_upvoted;
    setVoteAnim(p => ({ ...p, [complaintId]: wasVoted ? 'voteUndo 0.3s ease-out' : 'votePop 0.45s cubic-bezier(0.34,1.56,0.64,1)' }));
    if (!wasVoted) {
      setVotePlus(p => ({ ...p, [complaintId]: true }));
      setTimeout(() => setVotePlus(p => ({ ...p, [complaintId]: false })), 650);
    }
    setTimeout(() => setVoteAnim(p => ({ ...p, [complaintId]: '' })), 460);
    try {
      const res = await api.post('/community/upvote/' + complaintId);
      const { upvotes, user_has_upvoted } = res.data;
      setCommunity(prev => {
        const update = list => list ? list.map(c => c.id === complaintId ? { ...c, upvotes, user_has_upvoted } : c) : list;
        return { ...prev, dept_complaints: update(prev.dept_complaints), uni_complaints: update(prev.uni_complaints) };
      });
    } catch (e) {
      console.error('Upvote failed', e);
    }
  };

  if (loading) return <LoadingScreen />;

  const stats = {
    total:    complaints.length,
    pending:  complaints.filter(c => c.status === 'pending').length,
    verified: complaints.filter(c => c.status === 'verified').length,
    resolved: complaints.filter(c => c.status === 'resolved').length,
    rejected: complaints.filter(c => c.status === 'rejected').length,
  };

  const myDept   = complaints.filter(c => c.complaint_type === 'department');
  const myUni    = complaints.filter(c => c.complaint_type === 'university');
  // Sort: overdue first, then by date
  const myActive = (myTab === 'dept' ? myDept : myUni)
    .sort((a, b) => {
      if (a.is_overdue && !b.is_overdue) return -1;
      if (!a.is_overdue && b.is_overdue) return 1;
      return new Date(b.created_at) - new Date(a.created_at);
    })
    .slice(0, 8);

  const commDept = community?.dept_complaints || [];
  const commUni  = community?.uni_complaints  || [];
  const activeComplaints = communityTab === 'dept' ? commDept : commUni;

  const tabStyle = (active, color) => ({
    padding: '5px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 600,
    border: '1px solid ' + (active ? (color || COLORS.blue) : 'rgba(255,255,255,0.15)'),
    background: active ? (color || COLORS.blue) + '33' : 'rgba(255,255,255,0.05)',
    color: active ? (color || COLORS.blue) : 'rgba(255,255,255,0.6)',
    transition: 'all 0.2s',
  });

  return (
    <div style={s.page}>
      <div style={s.bgGrid} />
      <div style={s.bgOrb1} />
      <div style={s.bgOrb2} />
      <div style={s.bgOrb3} />
      <div style={s.layout}>
        <Sidebar mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : 248 }}>
          <Topbar
            title={'Welcome, ' + (user?.name?.split(' ')[0] || 'User')}
            subtitle={(user?.role === 'faculty' ? 'Faculty' : 'Student') + ' Portal - ' + (user?.department || '')}
            onMobileMenu={() => setMobileOpen(true)}
            actions={
              <button className="ur-btn-hover" style={s.btnPrimary} onClick={() => navigate('/complaints/new')}>
                + New Complaint
              </button>
            }
          />
          <div style={s.content}>
            {error && <div style={s.alertErr}>{error}</div>}
            <UserProfileCard user={user} />

            <div style={{ ...s.grid4, marginBottom: 20 }}>
              <StatCard label="Total Submitted" value={stats.total}    color={COLORS.purple} delay="0.05s" />
              <StatCard label="Pending"          value={stats.pending}  color={COLORS.amber}  delay="0.1s" />
              <StatCard label="Verified"         value={stats.verified} color={COLORS.blue}   delay="0.15s" />
              <StatCard label="Resolved"         value={stats.resolved} color={COLORS.green}  delay="0.2s" />
              <StatCard label="Rejected"         value={stats.rejected} color={COLORS.red}    delay="0.25s" />
            </div>

            <SectionCard
              title="My Complaints"
              subtitle="Your recent submissions"
              delay="0.3s"
              action={
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <LiveDot />
                  <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate('/complaints')}>View All</button>
                </div>
              }
            >
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <button style={tabStyle(myTab === 'dept', COLORS.blue)} onClick={() => setMyTab('dept')}>
                  Department ({myDept.length})
                </button>
                <button style={tabStyle(myTab === 'uni', COLORS.purple)} onClick={() => setMyTab('uni')}>
                  University-Wide ({myUni.length})
                </button>
              </div>
              {!myActive.length ? (
                <EmptyState
                  text={myTab === 'dept' ? 'No department complaints yet' : 'No university-wide complaints yet'}
                  sub="Submit your first complaint to get started"
                />
              ) : (
                <div style={{ overflowX: 'auto' }}>
                <table style={s.table}>
                  <thead style={s.thead}>
                    <tr>
                      <th style={s.th}>Title</th>
                      <th style={s.th}>Status</th>
                      <th style={s.th}>{myTab === 'dept' ? 'Department' : 'Category'}</th>
                      <th style={s.th}>Deadline</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myActive.map(c => (
                      <tr key={c.id} className="ur-row-hover"
                        style={c.is_overdue ? { background: 'rgba(239,68,68,0.07)', borderLeft: '2px solid #ef4444' } : {}}
                        onClick={() => navigate('/complaints/' + c.id)}>
                        <td style={s.tdBold}>
                          {c.is_overdue && (
                            <span style={{ marginRight: 6, fontSize: 10, padding: '1px 6px', borderRadius: 4,
                              background: 'rgba(239,68,68,0.2)', color: '#fca5a5', fontWeight: 700 }}>OVERDUE</span>
                          )}
                          {c.title}
                          {c.university_category && (
                            <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 6px', borderRadius: 4,
                              background: 'rgba(139,92,246,0.2)', color: '#c4b5fd' }}>
                              {c.university_category}
                            </span>
                          )}
                        </td>
                        <td style={s.td}><StatusBadge status={c.status} /></td>
                        <td style={s.td}>
                          <span style={{ fontSize: 12 }}>
                            {myTab === 'dept' ? (c.department || '-') : (c.university_category || '-')}
                          </span>
                        </td>
                        <td style={s.td}>
                          <span style={{ fontSize: 12 }}>
                            {c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}
                          </span>
                          {c.status === 'pending' && c.verification_deadline && (
                            <div><ComplaintTimer deadline={c.verification_deadline} isOverdue={false} /></div>
                          )}
                          {c.status === 'verified' && c.resolution_deadline && (
                            <div><ComplaintTimer deadline={c.resolution_deadline} isOverdue={c.is_overdue} label={c.is_overdue ? 'overdue' : ''} /></div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </SectionCard>

            <div style={{ marginTop: 16 }}>
              <SectionCard
                title="Community Board"
                subtitle={(user?.department || 'Department') + ' + university-wide complaints'}
                delay="0.38s"
                action={
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <LiveDot />
                    <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate('/community')}>Full Board</button>
                  </div>
                }
              >
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  <button style={tabStyle(communityTab === 'dept')} onClick={() => setCommunityTab('dept')}>
                    Department ({commDept.length})
                  </button>
                  <button style={tabStyle(communityTab === 'uni')} onClick={() => setCommunityTab('uni')}>
                    University-Wide ({commUni.length})
                  </button>
                </div>
                {communityLoading ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
                    Loading community...
                  </div>
                ) : !activeComplaints.length ? (
                  <EmptyState
                    text={communityTab === 'dept' ? 'No department complaints yet' : 'No university-wide complaints yet'}
                    sub="Be the first to submit one"
                  />
                ) : (
                  <table style={s.table}>
                    <thead style={s.thead}>
                      <tr>
                        <th style={s.th}>Title</th>
                        <th style={s.th}>Status</th>
                        <th style={s.th}>Department</th>
                        <th style={s.th}>Votes</th>
                        <th style={s.th}>Submitted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeComplaints.slice(0, 8).map(c => {
                        const voted = c.user_has_upvoted;
                        return (
                          <tr key={c.id} className="ur-row-hover"
                            style={c.is_overdue ? { background: 'rgba(239,68,68,0.07)' } : {}}
                            onClick={() => navigate('/complaints/' + c.id)}>
                            <td style={s.tdBold}>
                              {c.is_overdue && (
                                <span style={{ marginRight: 5, fontSize: 9, padding: '1px 5px', borderRadius: 3,
                                  background: 'rgba(239,68,68,0.2)', color: '#fca5a5', fontWeight: 700 }}>OVERDUE</span>
                              )}
                              {c.title}
                              {c.university_category && (
                                <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 6px', borderRadius: 4,
                                  background: 'rgba(139,92,246,0.2)', color: '#c4b5fd' }}>
                                  {c.university_category}
                                </span>
                              )}
                            </td>
                            <td style={s.td}><StatusBadge status={c.status} /></td>
                            <td style={s.td}>
                              <span style={{ fontSize: 12 }}>
                                {c.department || c.university_category || '-'}
                              </span>
                            </td>
                            <td style={s.td} onClick={e => e.stopPropagation()}>
                              <div style={{ position: 'relative', display: 'inline-flex' }}>
                                {votePlus[c.id] && (
                                  <span style={{ position: 'absolute', inset: 0, borderRadius: 7,
                                    border: '2px solid ' + COLORS.purple,
                                    animation: 'voteRipple 0.5s ease-out forwards', pointerEvents: 'none' }} />
                                )}
                                {votePlus[c.id] && (
                                  <span style={{ position: 'absolute', top: -4, left: '50%', fontSize: 11,
                                    fontWeight: 800, color: '#c4b5fd',
                                    animation: 'votePlusOne 0.65s ease-out forwards',
                                    pointerEvents: 'none', whiteSpace: 'nowrap', zIndex: 10 }}>+1</span>
                                )}
                                <button
                                  onClick={e => { e.stopPropagation(); handleUpvote(c.id); }}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 4,
                                    padding: '3px 8px', borderRadius: 7,
                                    cursor: 'pointer', fontSize: 11, fontWeight: 600,
                                    border: '1px solid ' + (voted ? COLORS.purple : 'rgba(255,255,255,0.2)'),
                                    background: voted ? 'rgba(139,92,246,0.2)' : 'rgba(255,255,255,0.06)',
                                    color: voted ? '#c4b5fd' : 'rgba(255,255,255,0.55)',
                                    transition: 'background 0.2s, border-color 0.2s, color 0.2s',
                                    animation: voteAnim[c.id] || 'none',
                                  }}
                                >
                                  <span>{voted ? 'v' : '^'}</span>
                                  {c.upvotes || 0}
                                </button>
                              </div>
                            </td>
                            <td style={s.td}>
                              <span style={{ fontSize: 12 }}>
                                {c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </SectionCard>
            </div>

            <div style={{ marginTop: 16 }}>
              <SectionCard title="Quick Actions" delay="0.45s">
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <button className="ur-btn-hover" style={s.btnPrimary} onClick={() => navigate('/complaints/new')}>Submit Complaint</button>
                  <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate('/complaints')}>All My Complaints</button>
                  <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate('/community')}>Community Board</button>
                  <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate('/profile')}>My Profile</button>
                </div>
              </SectionCard>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  return <ThemeProvider><DashboardInner /></ThemeProvider>;
}
